import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
	UrcUserInput,
	UrcResult,
	UrcNotSatisfiableError,
	RecipeNotSatisfiableApiError,
} from '../models/universal-recipe-calculator.model';
import { RestApiService } from './rest-api.service';
import { RecipeMode } from '../app.enums';

@Injectable({
	providedIn: 'root',
})
export class UniversalRecipeCalculatorService {
	constructor(public http: RestApiService) {}

	async calculateRecipe(mode: RecipeMode, input: UrcUserInput): Promise<UrcResult> {
		if (mode === RecipeMode.MP) {
			return {
				mode: RecipeMode.MP,
				finalVolume: input.finalDesiredVolumeInput?.desiredVolumeInMl ?? 0,
				finalCaloricDensityKcalPerOz: 0,
				// Manual Prep never calls the calculator; the modal supplies the selected density.
				finalCaloricDensityKcalPerMl: 0,
				products: input.products.map(p => ({
					productId: p.id,
					name: p.name,
					amountNeeded: 0,
					unitOfMeasure: '',
					caloricDensity: 0,
					fortifierVolumeInMl: 0,
					fortifierVolumeInOz: 0,
				})),
			};
		}

		const apiInput: UrcUserInput = {
			products: input.products,
			caloricDensityUnit: input.caloricDensityUnit,
		};
		if (mode === RecipeMode.VOH) {
			apiInput.volumeOnHandInput = input.volumeOnHandInput;
		} else {
			apiInput.finalDesiredVolumeInput = input.finalDesiredVolumeInput;
		}

		try {
			const result = await lastValueFrom<UrcResult>(
				this.http.post('/urc/calculator', apiInput),
			);
			return { ...result, mode };
		} catch (error) {
			// A 422 means no recipe can satisfy the products' UOM constraints (e.g. not enough base
			// on hand). Surface it as a typed error so callers can show an actionable message.
			const body = (error as HttpErrorResponse)?.error as
				| UrcNotSatisfiableError
				| undefined;
			if ((error as HttpErrorResponse)?.status === 422 && body?.reason) {
				throw new RecipeNotSatisfiableApiError(body);
			}
			throw error;
		}
	}
}
